import { ColumnDef, Row, createColumnHelper } from "@tanstack/react-table";
import { Layout } from "../../components/Layout/Layout";
import { useMemo, useEffect, useState } from "react";
import { Flex, Text, IconButton, useDisclosure, Button, Icon } from "@chakra-ui/react";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { useAppDispatch, useAppSelector } from "../../store";
import { fetchAllDepartments, fetchSingleDepartment, resetCurrentDepartment } from "../../store/slices/departments";
import { ConfirmDeleteRecruiterModal, MoveRecruiterModal, SelectRecruiterRoleModal, AddRecruiterModal } from "@/components/DepartmentModals";
import { EditIcon, DeleteIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { UserRole } from "api/auth/types";
import { selectUserRole } from "@/store/slices/user";
import { DepartmentRecruiter } from "@/model/department";
import { FiPlus } from "react-icons/fi";

const columnHelper = createColumnHelper<DepartmentRecruiter>();

const PostCell = ({ row }: { row: Row<any> }) => {
  let result: string = "";
  switch (row.original.role) {
    case UserRole.RECRUITER:
      result = "Рекрутер";
      break;
    case UserRole.ADMIN:
      result = "Администратор";
      break;
    case UserRole.ACCOUNT_MANAGER:
      result = "Аккаунт-менеджер";
      break;
    case UserRole.DELIVERY_MANAGER:
      result = "Деливери-менеджер";
      break;
    case UserRole.HRD:
      result = "HR директор";
      break;
    case UserRole.DEPARTMENT_MANAGER:
      result = "Руководитель подразделения";
      break;
  }
  return <Text>{result}</Text>;
};

export const MyDivisionPage = () => {
  const dispatch = useAppDispatch();
  const { currentDepartment, isLoading } = useAppSelector((state) => state.departments);
  const role = useAppSelector(selectUserRole);

  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isRoleOpen, onOpen: onRoleOpen, onClose: onRoleClose } = useDisclosure();
  const { isOpen: isMoveOpen, onOpen: onMoveOpen, onClose: onMoveClose } = useDisclosure();
  const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onClose: onAddModalClose } = useDisclosure();
  const [selectedRecruiter, setSelectedRecruiter] = useState<DepartmentRecruiter | null>(null);

  useEffect(() => {
    if (localStorage.getItem("department_id") !== "null") {
      dispatch(fetchSingleDepartment(Number(localStorage.getItem("department_id"))));
    }
    dispatch(fetchAllDepartments());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetCurrentDepartment());
    };
  }, [dispatch]);

  const ActionCell = ({ row }: { row: Row<any> }) => {
    return (
      <Flex gap="8px">
        {role === UserRole.ADMIN && (
          <IconButton
            aria-label="Выбрать роль"
            variant="ghost"
            size="sm"
            icon={<EditIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecruiter(row.original as DepartmentRecruiter);
              onRoleOpen();
            }}
          />
        )}
        {currentDepartment?.canRemoveRecruiter && (
          <IconButton
            aria-label="Удалить"
            variant="ghost"
            colorScheme="red"
            size="sm"
            icon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecruiter(row.original as DepartmentRecruiter);
              onDeleteOpen();
            }}
          />
        )}
        {currentDepartment?.canMoveRecruiter && (
          <IconButton
            aria-label="Переместить"
            variant="ghost"
            size="sm"
            icon={<ExternalLinkIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecruiter(row.original as DepartmentRecruiter);
              onMoveOpen();
            }}
          />
        )}
      </Flex>
    );
  };

  const columns = useMemo<Array<ColumnDef<DepartmentRecruiter, any>>>(() => {
    const cols: Array<ColumnDef<DepartmentRecruiter, any>> = [
      columnHelper.accessor("name", {
        header: "Сотрудник",
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
      }),
      columnHelper.accessor("post", {
        header: "Роль",
        cell: (info) => <PostCell row={info.row} />,
        enableColumnFilter: true,
      }),
      columnHelper.accessor("email", {
        header: "Контакты",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
      }),
    ];

    if (role === UserRole.DEPARTMENT_MANAGER) {
      cols.push(
        columnHelper.display({
          id: "action",
          header: "Действия",
          cell: (info) => <ActionCell row={info.row} />,
        }),
      );
    }

    return cols;
  }, [role, ActionCell]);
  return (
    <Layout>
      <>
        {currentDepartment ? (
          <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="16px">
            <Text fontSize={27} fontWeight={500}>
              Мое подразделение: {currentDepartment?.name}
            </Text>
            <Flex justifyContent="space-between" alignItems="center" paddingRight="16px">
              <Text fontSize={16} fontWeight={500}>
                Информация о подразделении
              </Text>
              {currentDepartment?.canAddFreeRecruiter && (
                <Button
                  isDisabled={!currentDepartment?.canAddFreeRecruiter}
                  onClick={onAddModalOpen}
                  bg="#2563eb"
                  color="white"
                  _hover={{ bg: "#2563eb" }}
                  _active={{ bg: "#2563eb" }}
                  leftIcon={<Icon as={FiPlus as any} boxSize={4} />}
                >
                  Добавить рекрутера
                </Button>
              )}
            </Flex>
            <BaseTable list={currentDepartment?.recruiters ?? []} columns={columns} isLoading={isLoading} withoutNavigate={true}/>
            <ConfirmDeleteRecruiterModal
              isOpen={isDeleteOpen}
              onClose={onDeleteClose}
              recruiterFullName={selectedRecruiter?.name || ""}
              recruiterId={selectedRecruiter?.id}
              departmentId={currentDepartment?.id}
            />
            <SelectRecruiterRoleModal
              isOpen={isRoleOpen}
              onClose={onRoleClose}
              onSave={() => onRoleClose()}
              recruiterFullName={selectedRecruiter?.name || ""}
              recruiterId={selectedRecruiter?.id || 0}
              roles={[
                { value: UserRole.RECRUITER, label: "Рекрутер" },
                { value: UserRole.DEPARTMENT_MANAGER, label: "Руководитель подразделения" },
                { value: UserRole.ACCOUNT_MANAGER, label: "Аккаунт-менеджер" },
                { value: UserRole.DELIVERY_MANAGER, label: "Деливери-менеджер" },
                { value: UserRole.HRD, label: "HRD" },
                { value: UserRole.ADMIN, label: "Админ" },
              ]}
              defaultRole={selectedRecruiter?.role}
              departmentId={currentDepartment?.id}
            />
            <MoveRecruiterModal
              isOpen={isMoveOpen}
              onClose={onMoveClose}
              onMove={() => onMoveClose()}
              recruiterFullName={selectedRecruiter?.name || ""}
              recruiterId={selectedRecruiter?.id || 0}
              departmentId={currentDepartment?.id}
            />
            <AddRecruiterModal isOpen={isAddModalOpen} onClose={onAddModalClose} departmentId={currentDepartment?.id} />
          </Flex>
        ) : (
          <Flex gap="64px" textAlign="left" flexDirection="column" height="100%">
            <Text w="100%" fontSize={27} fontWeight={500}>
              Мое подразделение: Default
            </Text>
            <Text w="100%" fontSize={27} fontWeight={500}>
              Вы пока не назначены в подразделение
            </Text>
            <Text w="100%" fontSize={27} fontWeight={500}>
              Вы находитесь в подразделении по умолчанию (Default).
              <br />
              Руководитель подразделения должен назначить вас в команду.
            </Text>
          </Flex>
        )}
      </>
    </Layout>
  );
};
